<template>
  <section class="px-4 mx-auto w-full max-w-5xl xl:px-0 md:px-6">
    <h2 class="mt-10 text-2xl text-center md:leading-10 xl:mt-20 xl:text-42 md:text-4xl font-ww-bold" v-html="mainTitle"></h2>

    <div class="mx-auto mt-5 max-w-3xl text-base text-left md:mt-10 xl:text-xl md:text-lg font-ww-regular lg:text-left">
      {{ introText }}

      <ul class="pl-6 mt-3 list-disc text-left md:mt-6 custom-marker-small">
        <li v-for="(item, index) in listItems" :key="index">
          {{ typeof item === 'string' ? item : item.item }}
        </li>
      </ul>

      <div class="mt-4" v-html="closingText"></div>
    </div>

    <div class="grid grid-cols-12 gap-x-3 gap-y-4 mt-10 xl:mt-16">
      <div class="relative col-span-12 sm:col-span-6">
        <nuxt-img
          :src="leftImageSrc"
          :alt="leftImageAlt"
          class="object-cover w-full sm-max:rounded-xl"
          format="webp"
          loading="lazy"
          width="490"
          height="632"
          sizes="xs:291px sm:393px md:354px lg:474px xl:482px"
        />
        
        <div class="absolute bottom-0 left-1/2 w-full text-center -translate-x-1/2">
          <nuxt-img
            :src="leftIconSrc"
            :alt="leftIconAlt"
            class="mx-auto w-10 lg:w-auto"
            format="webp"
            loading="lazy"
            width="55"
            height="55"
              sizes="xs:291px sm:393px md:354px lg:474px xl:482px"
          />

          <div class="pb-4 mt-2 text-xl text-white md:pb-12 sm:text-2xl md-max:leading-7 md:mt-5 xl:text-4xl md:text-3xl font-ww-bold" v-html="leftText"></div>
        </div>
      </div>

      <div class="relative col-span-12 sm:col-span-6">
        <nuxt-img
          :src="rightImageSrc"
          :alt="rightImageAlt"
          class="object-cover w-full sm-max:rounded-xl"
          format="webp"
          loading="lazy"
          width="490"
          height="632"
        />

        <div class="absolute bottom-0 left-1/2 w-full text-center -translate-x-1/2">
          <nuxt-img
            :src="rightIconSrc"
            :alt="rightIconAlt"
            class="mx-auto w-10 lg:w-auto"
            format="webp"
            loading="lazy"
            width="55"
            height="55"
          />

          <div class="pb-4 mt-2 text-xl text-white md:pb-12 sm:text-2xl md-max:leading-7 md:mt-5 xl:text-4xl md:text-3xl font-ww-bold" v-html="rightText"></div>
        </div>
      </div>
    </div>

    <div class="mt-5 text-base text-center lg:mt-10 xl:text-2xl md:text-xl font-ww-bold">
      {{ ctaHeading }}
    </div>

    <div class="flex justify-center pb-10 md:pb-20">
      <button
        @click="scrollTo(scrollTarget, scrollPosition(mobilePosition))"
        type="button"
        :style="{ background: primaryColor }"
        class="text-white bnt-basic items-center mt-8 flex xl:!py-5 font-bold !text-lg md:!py-4 !py-3 gap-x-2 !capitalize md-max:px-8 md:!text-xl lg:!text-3xl md:!px-16"
        :aria-label="ctaText"
      >
        <span>{{ ctaText }}</span>
        <svg
          width="28"
          height="23"
          viewBox="0 0 28 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="relative top-0.5 w-5 md:w-6 lg:top-1 lg:w-auto"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M27.3403 12.5758L17.9164 22.0853C17.787 22.2166 17.6327 22.321 17.4625 22.3922C17.2923 22.4634 17.1096 22.5 16.925 22.5C16.7405 22.5 16.5578 22.4634 16.3876 22.3922C16.2174 22.321 16.0631 22.2166 15.9337 22.0853C15.6706 21.8187 15.5231 21.4594 15.5231 21.0852C15.5231 20.7109 15.6706 20.3517 15.9337 20.0851L22.905 13.0496H1.90953C1.60041 13.0478 1.30464 12.9237 1.08722 12.7043C0.869793 12.4849 0.748502 12.1883 0.750014 11.8797V11.3841C0.748502 11.0755 0.869793 10.7789 1.08722 10.5596C1.30464 10.3402 1.60041 10.216 1.90953 10.2143H23.1608L15.9289 2.91494C15.6659 2.6483 15.5184 2.28907 15.5184 1.91482C15.5184 1.54056 15.6659 1.18133 15.9289 0.914689C16.0583 0.783352 16.2126 0.679029 16.3828 0.607828C16.553 0.536628 16.7358 0.5 16.9203 0.5C17.1048 0.5 17.2875 0.536628 17.4577 0.607828C17.6279 0.679029 17.7822 0.783352 17.9116 0.914689L27.3233 10.4119C27.5559 10.6493 27.6968 10.9612 27.7211 11.2924C27.7679 11.5211 27.7577 11.7578 27.6913 11.9816C27.6249 12.2054 27.5043 12.4095 27.3403 12.5758Z"
            fill="white"
          />
        </svg>
      </button>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  mainTitle: {
    type: String,
    default: "\"It's Not Just Fatigue… <br/>It's the Fear of Losing Your Edge as You Age.\""
  },
  introText: {
    type: String,
    default: "If you've noticed:"
  },
  listItems: {
    type: Array,
    default: () => [
      "Your morning coffee doesn't give you the same lift it used to…",
      "By mid-afternoon, you're running on fumes (or reaching for sugar)…",
      "Your mind feels foggy, and words or names slip more often than they used to…",
      "Regular coffee leaves your stomach uneasy or bloated…"
    ]
  },
  closingText: {
    type: String,
    default: "You're not alone. These changes are common for women in their 40s, 50s, and beyond.<br><br>But here's the truth: <strong>you don't have to just \"accept\" them.</strong><br><br>Everyday choices — even the coffee you drink — can play a role in supporting your brain health, memory, and long-term vitality."
  },
  leftImageSrc: {
    type: String,
    default: "/assets/images/sec-cog-1.png"
  },
  leftImageAlt: {
    type: String,
    default: "Woman looking tired from drinking wrong coffee"
  },
  leftIconSrc: {
    type: String,
    default: "/assets/images/x-img.png"
  },
  leftIconAlt: {
    type: String,
    default: "X mark icon"
  },
  leftText: {
    type: String,
    default: "The Wrong Coffee <br> Can Drain You..."
  },
  rightImageSrc: {
    type: String,
    default: "/assets/images/sec-cog-2.png"
  },
  rightImageAlt: {
    type: String,
    default: "Woman feeling energized from drinking right coffee"
  },
  rightIconSrc: {
    type: String,
    default: "/assets/images/check-img.png"
  },
  rightIconAlt: {
    type: String,
    default: "Check mark icon"
  },
  rightText: {
    type: String,
    default: "The Right One Can <br> Support You..."
  },
  ctaHeading: {
    type: String,
    default: "Discover Mushroom Coffee That Supports Your Brain & Body"
  },
  ctaText: {
    type: String,
    default: "Try It Risk-Free Today"
  },
  primaryColor: {
    type: String,
    default: "#ff9315"
  },
  scrollTarget: {
    type: String,
    default: "the-products"
  },
  mobilePosition: {
    type: String,
    default: "bottom"
  }
})

defineBuilderComponent({
  friendlyName: "Content - Text Description Compare Block",
  inputs: [
    {
      name: "mainTitle",
      type: "richtext",
      defaultValue: "\"It's Not Just Fatigue… <br/>It's the Fear of Losing Your Edge as You Age.\""
    },
    {
      name: "introText",
      type: "text",
      defaultValue: "If you've noticed:"
    },
    {
      name: "listItems",
      type: "list",
      subFields: [
        {
          name: "item",
          type: "text"
        }
      ],
      defaultValue: [
        { item: "Your morning coffee doesn't give you the same lift it used to…" },
        { item: "By mid-afternoon, you're running on fumes (or reaching for sugar)…" },
        { item: "Your mind feels foggy, and words or names slip more often than they used to…" },
        { item: "Regular coffee leaves your stomach uneasy or bloated…" }
      ]
    },
    {
      name: "closingText",
      type: "richtext",
      defaultValue: "You're not alone. These changes are common for women in their 40s, 50s, and beyond.<br><br>But here's the truth: <strong>you don't have to just \"accept\" them.</strong><br><br>Everyday choices — even the coffee you drink — can play a role in supporting your brain health, memory, and long-term vitality."
    },
    {
      name: "leftImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/sec-cog-1.png"
    },
    {
      name: "leftImageAlt",
      type: "text",
      defaultValue: "Woman looking tired from drinking wrong coffee"
    },
    {
      name: "leftIconSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp", "svg"],
      defaultValue: "/assets/images/x-img.png"
    },
    {
      name: "leftIconAlt",
      type: "text",
      defaultValue: "X mark icon"
    },
    {
      name: "leftText",
      type: "text",
      defaultValue: "The Wrong Coffee <br> Can Drain You..."
    },
    {
      name: "rightImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/sec-cog-2.png"
    },
    {
      name: "rightImageAlt",
      type: "text",
      defaultValue: "Woman feeling energized from drinking right coffee"
    },
    {
      name: "rightIconSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp", "svg"],
      defaultValue: "/assets/images/check-img.png"
    },
    {
      name: "rightIconAlt",
      type: "text",
      defaultValue: "Check mark icon"
    },
    {
      name: "rightText",
      type: "text",
      defaultValue: "The Right One Can <br> Support You..."
    },
    {
      name: "ctaHeading",
      type: "text",
      defaultValue: "Discover Mushroom Coffee That Supports Your Brain & Body"
    },
    {
      name: "ctaText",
      type: "text",
      defaultValue: "Try It Risk-Free Today"
    },
    {
      name: "primaryColor",
      type: "color",
      defaultValue: "#ff9315"
    },
    {
      name: "scrollTarget",
      type: "text",
      defaultValue: "the-products"
    },
    {
      name: "mobilePosition",
      type: "enum",
      friendlyName: "Scroll Position Mobile",
      enum: [
        { label: "Bottom", value: "bottom" },
        { label: "Top", value: "top" }
      ],
      defaultValue: "bottom"
    }
  ]
})
</script>
